<template>
  <div id="recurringDiv">
    <b-row v-bind="$attrs">
      <b-col cols="12">
        <h5>
          {{ recurringStatement }}
        </h5>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { currencyFormat, awsDateToDate } from "@/services/Formatting.js";
import informationTemplate from "@/components/recurringpayment/recurringPaymentInformation.json";

export default {
  props: {
    recurringInfo: {
      period: {
        type: Number,
        default: 0
      },
      frequency: {
        type: String,
        default: ""
      },
      reason: {
        type: String,
        default: ""
      },
      executionDate: {
        type: String,
        default: ""
      }
    },
    orderDetails: {
      amount: {
        type: Number,
        default: 0
      },
      currency: {
        type: String,
        default: ""
      },
      initialPaymentAmount: {
        type: Number,
        default: 0
      }
    },
    paymentFrequencies: {
      type: Object,
      default() {
        return {
          Daily: "Daily",
          Weekly: "Weekly",
          Fortnightly: "Fortnightly/Bi-Weekly",
          Monthly: "Monthly",
          BiMonthly: "Bi-Monthly",
          Quarterly: "Quarterly",
          Yearly: "Yearly"
        };
      }
    }
  },
  computed: {
    recurringStatement() {
      return this.generateRecurringStatement();
    },
    currencyText() {
      return this.orderDetails.currency;
    },
    hasInitialPaymentAmount() {
      return this.orderDetails.initialPaymentAmount > 0;
    },
    isInitialPaymentEqualToRecurringPayment() {
      return (
        this.hasInitialPaymentAmount &&
        this.orderDetails.initialPaymentAmount === this.orderDetails.amount
      );
    },
    recurringAmount() {
      return currencyFormat(this.orderDetails.amount);
    },
    initialPaymentAmount() {
      return currencyFormat(this.orderDetails.initialPaymentAmount);
    },
    executionDate() {
      const executionDateString = this.recurringInfo.executionDate;
      if (!executionDateString) return new Date();

      return awsDateToDate(executionDateString);
    }
  },
  methods: {
    generateRecurringStatement() {
      const template = this.setupTemplate();
      const obj = this.setupObject();
      return this.generateStatement(obj, template);
    },
    setupTemplate() {
      const {
        beginingPhrase,
        continuingPhrase,
        recurringPhrase,
        recurringPeriodObject,
        recurringEnding
      } = informationTemplate;

      const recurringPeriod =
        recurringPeriodObject[this.recurringInfo.frequency];
      if (!this.hasInitialPaymentAmount) {
        return `${beginingPhrase} ${recurringPhrase} ${recurringPeriod}.`;
      }

      if (this.isInitialPaymentEqualToRecurringPayment) {
        return `${beginingPhrase} ${continuingPhrase} ${recurringPhrase} ${recurringPeriod}.`;
      }

      return `${beginingPhrase} ${continuingPhrase} ${recurringPeriod} ${recurringEnding}.`;
    },
    setupObject() {
      const firstAmount = this.hasInitialPaymentAmount
        ? this.initialPaymentAmount
        : this.recurringAmount;
      const secondAmount = this.recurringAmount;

      const { day, weekDay, month } = this.parseDate(this.executionDate);

      return {
        firstAmount,
        secondAmount,
        currency: this.currencyText,
        day,
        weekDay,
        month
      };
    },
    parseDate(date) {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];

      const weekDay = days[date.getDay()];
      const day = this.getDayWithOrdinal(date);
      const month = months[date.getMonth()];

      return { day, weekDay, month };
    },
    getDayWithOrdinal(date) {
      const day = date.getDate();
      let ordinal = "th";

      if (day <= 3 && day >= 21) {
        switch (d % 10) {
          case 1:
            ordinal = "st";
          case 2:
            ordinal = "nd";
          case 3:
            ordinal = "rd";
          default:
            ordinal = "th";
        }
      }

      return `${day}${ordinal}`;
    },
    generateStatement(obj, template) {
      const generate = new Function(`return \`${template}\`;`);

      return generate.call(obj);
    }
  }
};
</script>

<style lang="scss" scoped>
#r {
  font-size: 15px;
  font-weight: 400;
}
</style>
